import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, Move } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getWhatsAppLink, WhatsAppMessages } from '../utils/whatsapp';
import { formatPrice } from '../utils/currency';
import { useCurrency } from '../contexts/CurrencyContext';
import SmartImage from './SmartImage';
import WhatsAppIcon from './WhatsAppIcon';

// eager: for the cards in the first row of a grid, which can be on screen at
// load — a lazy image there waits for layout before it even starts.
const PropertyCard = ({ project, eager = false }) => {
    const { t } = useTranslation();
    const { currency } = useCurrency();

    return (
        <div className="glass-card property-card">
            <div className="property-image-container">
                {/* Cards render ~300–420px wide in the grid; sizes tells the
                    browser that before it has any layout, so it never pulls the
                    1600px candidate for a phone. */}
                <SmartImage
                    src={project.feature_image}
                    alt={project.title}
                    className="property-image"
                    width={800}
                    height={500}
                    displayWidth={640}
                    sizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw"
                    {...(eager && { loading: 'eager' })}
                />
                <div className="property-status">
                    {t('card.for_sale')}
                </div>
                <div className="property-price-tag">
                    {project.min_price ? formatPrice(project.min_price, currency) : t('card.price_ask')}
                </div>
            </div>

            <div className="property-details">
                <h3 className="property-card-title">{project.title}</h3>

                <div className="property-location">
                    <MapPin size={16} color="var(--color-neon-blue)" />
                    {project.location || project.city_name}
                </div>

                <div className="property-card-specs">
                    <div className="spec-item">
                        <Bed size={16} />
                        <span>{project.beds || '2-4'} <span className="spec-label">{t('card.beds')}</span></span>
                    </div>
                    <div className="spec-item">
                        <Bath size={16} />
                        <span>{project.baths || '3'} <span className="spec-label">{t('card.baths')}</span></span>
                    </div>
                    <div className="spec-item">
                        <Move size={16} />
                        <span>{project.min_area || '1200'} <span className="spec-label">{t('card.sqft')}</span></span>
                    </div>
                </div>

                <div className="property-card-actions">
                    <Link
                        to={`/project/${project.id}`}
                        className="pc-btn pc-btn-details"
                    >
                        {t('card.view_details')}
                    </Link>
                    <a
                        href={getWhatsAppLink(WhatsAppMessages.projectInquiry(project.title, project.location || project.city_name))}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pc-btn pc-btn-wa"
                        aria-label={`WhatsApp about ${project.title}`}
                    >
                        <WhatsAppIcon size={16} color="#fff" />
                        <span>WhatsApp</span>
                    </a>
                </div>
            </div>

            <style>{`
                .property-card-actions {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 10px;
                    margin-top: 1.25rem;
                }

                .pc-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 11px 14px;
                    border-radius: 10px;
                    font-weight: 700;
                    font-size: 0.85rem;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    text-decoration: none;
                    cursor: pointer;
                    transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1),
                                box-shadow 0.25s ease,
                                background 0.25s ease,
                                border-color 0.25s ease;
                }

                .pc-btn-details {
                    background: rgba(0, 243, 255, 0.06);
                    color: var(--color-neon-blue);
                    border: 1px solid rgba(0, 243, 255, 0.5);
                }

                .pc-btn-details:hover {
                    background: rgba(0, 243, 255, 0.12);
                    border-color: var(--color-neon-blue);
                    transform: translateY(-1px);
                    box-shadow: 0 6px 18px rgba(0, 243, 255, 0.18);
                }

                .pc-btn-wa {
                    background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
                    color: #fff;
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    box-shadow: 0 6px 18px rgba(37, 211, 102, 0.28),
                                0 0 0 1px rgba(255, 255, 255, 0.06) inset;
                }

                .pc-btn-wa:hover {
                    transform: translateY(-1px);
                    box-shadow: 0 10px 24px rgba(37, 211, 102, 0.38),
                                0 0 0 1px rgba(255, 255, 255, 0.14) inset;
                    border-color: rgba(255, 255, 255, 0.18);
                }

                .pc-btn-wa:active,
                .pc-btn-details:active {
                    transform: translateY(0) scale(0.985);
                }

                @media (max-width: 480px) {
                    .pc-btn {
                        padding: 10px 8px;
                        font-size: 0.78rem;
                        letter-spacing: 0.03em;
                    }
                }
            `}</style>
        </div>
    );
};

export default PropertyCard;
